/**
 * @component ry-switch
 * @category  form
 * @summary   Binary on/off toggle. Wraps a hidden <input type="checkbox">.
 *
 * @attribute checked   boolean   default=false   Toggle state.
 * @attribute disabled  boolean   default=false   Non-interactive.
 * @attribute name      string?                    Form field name on the inner input.
 *
 * @property  checked   boolean   Mirrors `checked` attribute (get/set).
 * @property  value     string    Internal input value (form submit payload).
 *
 * @event ry:beforechange
 *        detail: { value: 'true' | 'false', label: 'on' | 'off', previous: 'true' | 'false' }
 *        fires:  from the inner input's `click` (mouse, touch, Space, Enter, label click) before the toggle commits. `value`/`label` describe what it is ABOUT to become; `previous` is the current state.
 *        cancelable: preventDefault() cancels the inner input's click — the platform leaves `checked` untouched and `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: 'true' | 'false', label: 'on' | 'off' }
 *        fires:  user toggle (mouse, touch, Space, Enter)
 *
 * @keyboard Space, Enter — toggles when focused.
 *
 * @content  Inline text becomes the visible label next to the track.
 *
 * @dom
 *   ry-switch
 *     > label.ry-switch__wrapper
 *       > input.ry-switch__input        (visually hidden)
 *       > span.ry-switch__track
 *         > span.ry-switch__thumb
 *       > span.ry-switch__label         (only if textContent provided)
 *
 * @gotcha The visually-hidden input is `position: absolute`. The host
 *         element is `position: relative` (set in ry-structure.css) so
 *         the input anchors here, not to <html>. Removing that rule causes
 *         many switches in scroll containers to inflate document.scrollHeight.
 *
 * @gotcha Setting `.checked` programmatically (`el.checked = true`) updates
 *         state SILENTLY — it does NOT fire `ry:change`. The event only
 *         fires from genuine user interaction (mouse/keyboard). If you need
 *         to fire ry:change from JS, dispatch a `change` event on the inner
 *         input — see the `programmatic-setter` tutorial.
 *
 * @example simple
 * ```html
 * <ry-switch>Enable notifications</ry-switch>
 * ```
 *
 * @example pre-checked
 * ```html
 * <ry-switch checked>Dark mode</ry-switch>
 * ```
 *
 * @example no-label
 * ```html
 * <ry-switch></ry-switch>
 * ```
 *
 * @tutorial basic
 * ```js
 * const el = mount('<ry-switch>Enable notifications</ry-switch>');
 * await expectEqual(el.checked, false, 'starts unchecked');
 *
 * // Simulate a user click. In a real browser, clicking the host's label
 * // toggles the inner checkbox and fires its `change` event, which
 * // ry-switch listens for and re-emits as `ry:change`. We do that flow
 * // explicitly here to work in any DOM environment.
 * const input = el.querySelector('input');
 * const toggle = () => {
 *   input.checked = !input.checked;
 *   input.dispatchEvent(new Event('change', { bubbles: true }));
 * };
 *
 * await expectEvent(el, 'ry:change', toggle, { value: 'true' });
 * await expectEqual(el.checked, true, 'first toggle: on');
 *
 * await expectEvent(el, 'ry:change', toggle, { value: 'false' });
 * await expectEqual(el.checked, false, 'second toggle: off');
 * ```
 *
 * @tutorial pre-checked
 * ```js
 * const el = mount('<ry-switch checked>Dark mode</ry-switch>');
 * await expectEqual(el.checked, true, 'starts checked when attribute is set');
 * await expectAttr(el, 'checked', '');
 * ```
 *
 * @tutorial programmatic-setter
 * ```js
 * // .checked= setter mutates state but does NOT fire ry:change. Important.
 * const el = mount('<ry-switch>x</ry-switch>');
 * let ryChangeFired = 0;
 * el.addEventListener('ry:change', () => ryChangeFired++);
 *
 * el.checked = true;
 * await expectEqual(el.checked, true, 'setter updates state');
 * await expectAttr(el, 'checked', '', 'setter syncs attribute');
 * await expectEqual(ryChangeFired, 0, 'setter does NOT fire ry:change');
 *
 * // To force the event from JS, dispatch on the inner input:
 * const input = el.querySelector('input');
 * await expectEvent(el, 'ry:change', () => {
 *   input.checked = false;
 *   input.dispatchEvent(new Event('change', { bubbles: true }));
 * }, { value: 'false' });
 * ```
 *
 * @tutorial disabled
 * ```js
 * const el = mount('<ry-switch disabled>x</ry-switch>');
 * await expectAttr(el, 'disabled', '');
 * // The hidden input's `disabled` reflects too — form submission omits it.
 * const input = el.querySelector('input');
 * await expectEqual(input.disabled, true, 'inner input is also disabled');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange cancels the inner input's click —
 * // the switch stays where it was and ry:change never fires. Enter/Space
 * // route through the same click, so the veto covers keyboard too.
 * const el = mount('<ry-switch>Notifications</ry-switch>');
 * const input = el.querySelector('input');
 * const changed = [];
 * el.addEventListener('ry:change', () => changed.push(1));
 * let seen = null;
 * el.addEventListener('ry:beforechange', (e) => { seen = e.detail; e.preventDefault(); }, { once: true });
 * input.click();
 * await expectEqual(seen.value, 'true', 'detail.value is the NEXT state');
 * await expectEqual(seen.previous, 'false', 'detail.previous is the current state');
 * await expectEqual(el.checked, false, 'vetoed click leaves it off');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * input.click();
 * await expectEqual(el.checked, true, 'without the veto, the click toggles');
 * await expectEqual(changed.length, 1, 'ry:change fired once');
 * ```
 *
 * @see ry-toggle-button, ry-checkbox, ry-radio
 */

import { RyElement } from '../core/ry-element.js';
import type { ChangeDetail } from '../types.js';

let switchIdCounter = 0;

interface SwitchBeforeChangeDetail extends ChangeDetail {
  previous: string;
}

export class RySwitch extends RyElement {
  #input: HTMLInputElement | null = null;

  static observedAttributes = ['checked', 'disabled'] as const;

  setup(): void {
    this.#transformMarkup();

    this.#input = this.$<HTMLInputElement>('input[type="checkbox"]');
    if (this.#input) {
      this.on(this.#input, 'click', this.#handleClick);
      this.on(this.#input, 'change', this.#handleChange);
    }

    // Keyboard support on the wrapper
    this.on(this, 'keydown', this.#handleKeydown);
  }

  #transformMarkup(): void {
    // Check if already transformed
    if (this.$('[data-ry-target="track"]')) return;

    const id = `ry-switch-${++switchIdCounter}`;
    const name = this.getAttribute('name') ?? '';
    const checked = this.hasAttribute('checked');
    const disabled = this.hasAttribute('disabled');
    const label = this.textContent?.trim() ?? '';

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <label data-ry-target="wrapper" class="ry-switch__wrapper" for="${id}">
        <input
          type="checkbox"
          id="${id}"
          data-ry-target="input"
          class="ry-switch__input"
          ${name ? `name="${name}"` : ''}
          ${checked ? 'checked' : ''}
          ${disabled ? 'disabled' : ''}
          role="switch"
          aria-checked="${checked}"
        >
        <span data-ry-target="track" class="ry-switch__track">
          <span data-ry-target="thumb" class="ry-switch__thumb"></span>
        </span>
        ${label ? `<span data-ry-target="label" class="ry-switch__label">${label}</span>` : ''}
      </label>
    `;
  }

  // The interception point. A checkbox's `click` pre-activation has ALREADY
  // flipped `checked` by the time the event dispatches, so `input.checked`
  // here is the NEXT value. Cancelling the click makes the platform restore
  // it and no native `change` fires. Enter/Space call `input.click()`, so
  // the keyboard path is covered by the same listener.
  #handleClick = (event: MouseEvent): void => {
    if (!this.#input) return;
    const next = this.#input.checked;
    if (!this.emit<SwitchBeforeChangeDetail>('beforechange', {
      value: String(next),
      label: next ? 'on' : 'off',
      previous: String(!next),
    })) event.preventDefault();
  };

  #handleChange = (): void => {
    if (!this.#input) return;

    const checked = this.#input.checked;
    this.#input.setAttribute('aria-checked', String(checked));

    if (checked) {
      this.setAttribute('checked', '');
    } else {
      this.removeAttribute('checked');
    }

    this.emit<ChangeDetail>('change', {
      value: String(checked),
      label: checked ? 'on' : 'off'
    });
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.#input?.click();
    }
  };

  get checked(): boolean {
    return this.#input?.checked ?? false;
  }

  set checked(value: boolean) {
    if (this.#input) {
      this.#input.checked = value;
      this.#input.setAttribute('aria-checked', String(value));

      if (value) {
        this.setAttribute('checked', '');
      } else {
        this.removeAttribute('checked');
      }
    }
  }

  get value(): string {
    return this.#input?.value ?? '';
  }

  set value(val: string) {
    if (this.#input) {
      this.#input.value = val;
    }
  }
}

customElements.define('ry-switch', RySwitch);
